import {
  type ComponentProps,
  type FormEvent,
  type ForwardedRef,
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';

import { Form as RacForm } from '@iconik-io/uad';

const formDataToObject = (formData: FormData): Record<string, any> => {
  const result: Record<string, any> = {};

  formData.forEach((value, key) => {
    if (result[key]) {
      if (Array.isArray(result[key])) {
        result[key].push(value);
      } else {
        result[key] = [result[key], value];
      }
    } else {
      result[key] = value;
    }
  });

  return result;
};

type Props<Data> = PropsWithChildren<
  Omit<ComponentProps<typeof RacForm>, 'onSubmit'> & {
    onSubmit?: (data: Data, reset: (data?: Data) => void) => void;
  }
>;

export const Form = forwardRef(function <Data>(
  props: Props<Data>,
  ref: ForwardedRef<HTMLFormElement>,
) {
  const formRef = useRef<HTMLFormElement>(null);

  // biome-ignore lint/plugin: the ref is only read after mount, when the form element exists.
  useImperativeHandle(ref, () => formRef.current as HTMLFormElement);

  const reset = useCallback(() => {
    formRef.current?.reset();
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    // biome-ignore lint/plugin: the caller declares the shape its form produces; FormData is untyped.
    const data = formDataToObject(formData) as Data;
    props.onSubmit?.(data, reset);
  };

  return (
    <RacForm {...props} onSubmit={handleSubmit} ref={formRef}>
      {props.children}
    </RacForm>
  );
});
